import React, { memo, useCallback, useMemo } from 'react';
import { IconZzz } from '@tabler/icons-react';
import { TranscriptPauseEntry } from './types';
import TranscriptPauseToolbar from './TranscriptPauseToolbar';
import useToolbarPosition from './useToolbarPosition';

interface TranscriptDeadSpaceRowProps {
  entries: TranscriptPauseEntry[];
  onOptimize: (entry: TranscriptPauseEntry) => void;
  onRecover: (entry: TranscriptPauseEntry) => void;
  onOptimizeAll?: () => void;
  onRestoreAll?: () => void;
  hasDeleteFlags: boolean[];
  isHighlighted?: boolean;
}

function TranscriptDeadSpaceRow({
  entries,
  onOptimize,
  onRecover,
  onOptimizeAll,
  onRestoreAll,
  hasDeleteFlags,
  isHighlighted
}: TranscriptDeadSpaceRowProps): JSX.Element {
  const { showToolbar, setShowToolbar, closeToolbar, toolbarPosition, buttonRef, toolbarRef } = useToolbarPosition();

  const allOptimized = hasDeleteFlags.every(Boolean);

  const totalDuration = useMemo(() => entries.reduce((sum, e) => sum + e.pauseDuration, 0), [entries]);
  const totalRecommended = useMemo(() => entries.reduce((sum, e) => sum + e.recommendedPause, 0), [entries]);

  const displayLabel = allOptimized ? `${totalRecommended.toFixed(1)}s` : `${totalDuration.toFixed(1)}s`;

  const handleOptimize = useCallback((): void => {
    entries.forEach((entry, i) => {
      if (!hasDeleteFlags[i]) {
        onOptimize(entry);
      }
    });
    closeToolbar();
  }, [entries, hasDeleteFlags, onOptimize, closeToolbar]);

  const handleRecover = useCallback((): void => {
    entries.forEach((entry, i) => {
      if (hasDeleteFlags[i]) {
        onRecover(entry);
      }
    });
    closeToolbar();
  }, [entries, hasDeleteFlags, onRecover, closeToolbar]);

  const handleOptimizeAll = useCallback((): void => {
    onOptimizeAll?.();
    closeToolbar();
  }, [onOptimizeAll, closeToolbar]);

  const handleRestoreAll = useCallback((): void => {
    onRestoreAll?.();
    closeToolbar();
  }, [onRestoreAll, closeToolbar]);

  const buttonClass = allOptimized
    ? 'bg-green-100 text-green-500 hover:bg-green-200'
    : 'bg-slate-200 text-slate-600 hover:bg-slate-300';

  return (
    <div className="ml-1 flex items-center gap-1 py-2">
      <IconZzz className="size-4 text-slate-500" />
      <button
        ref={buttonRef}
        type="button"
        data-testid="transcript-dead-space-row"
        data-pause-type="deadSpace"
        className={`inline-flex cursor-pointer items-center rounded-md px-2 py-0 text-[13px] font-normal leading-[22px] transition ${isHighlighted ? '!bg-[#ff4200] !text-white' : buttonClass}`}
        onClick={() => setShowToolbar(prev => !prev)}
      >
        <span>{displayLabel}</span>
      </button>

      {showToolbar && toolbarPosition && (
        <TranscriptPauseToolbar
          ref={toolbarRef}
          entry={entries[0]}
          hasDelete={allOptimized}
          position={toolbarPosition}
          onOptimize={handleOptimize}
          onRecover={handleRecover}
          onOptimizeAll={onOptimizeAll ? handleOptimizeAll : undefined}
          onRestoreAll={onRestoreAll ? handleRestoreAll : undefined}
        />
      )}
    </div>
  );
}

export default memo(TranscriptDeadSpaceRow);
